<template>
    <v-container class="ma-0 pa-0">
        <v-data-table
            :headers="computedHeaders"
            :items="husbandry"
            :loading="loading"
            :footer-props="footerProps"
            :server-items-length="totalHusbandryHistory"
            :sort-by.sync="pagination.sortBy"
            :sort-desc.sync="pagination.descending"
            :items-per-page.sync="pagination.rowsPerPage"
            :page.sync="pagination.page"
            must-sort
        >
            <v-progress-linear slot="progress" color="primary" indeterminate></v-progress-linear>
            <template slot="item" slot-scope="props">
                <tr :class="determineHightlightClass(props.item.Identifier)">
                    <td v-if="props.item.Type === 'Daily Husbandry'">{{props.item.EventDate | formatTasksDate}}</td>
                    <td v-else>{{props.item.EventDate | formatNonTasksDate}}</td>
                    <td v-if="displayUser">{{props.item.User}}</td>
                    <td v-if="displaySpecimenID">{{props.item.SpecimenDisplayID}}</td>
                    <td v-if="displayLocationWeight">{{props.item.Location}}</td>
                    <td v-if="displayLocationWeight">{{props.item.Weight}}</td>
                    
                    <td>{{props.item.Type}}</td>
                    <td class="display-new-line">{{props.item.Notes}}</td>
                    <td v-if="displayDelete && auth.currentUserIsAdmin()">
                        <v-btn
                            color="error"
                            block
                            @click="confirmDeleteDailyHusbandryRecord(props.item)"
                        >
                            Delete
                        </v-btn>
                    </td>
                    <td v-else></td>
                </tr>
            </template>
            <template v-if="!loading" slot="no-data">
                <v-alert v-if="loadError" outlined :value="true" color="error" icon="error">
                    {{loadError}}
                </v-alert>
                <v-alert v-if="!loadError" outlined :value="true" type="info">
                    No results found
                </v-alert>
            </template>
        </v-data-table>

        <v-dialog v-model="showDeleteConfirmDialog" persistent max-width="600">
            <v-card class="no-padding confirm-dialog">
                <v-card-title class="headline">Confirm Delete</v-card-title>
                <v-card-text>Are you sure you wish to remove this {{deletedDailyHusbandryRecord.Type}} record?</v-card-text>
                <v-card-text>This action cannot be undone.</v-card-text>
                <v-card-text>After deletion, this daily husbandry record will no longer appear in this list, but an audit record with the deleted information will still be available.</v-card-text>
                <v-card-actions>
                    <v-row >
                        <v-col cols="6">
                            <v-btn color="success" block @click="cancelDeleteDailyHusbandryRecord()" :loading="deleting">Cancel</v-btn>
                        </v-col>
                        <v-col cols="6">
                            <v-btn color="error" block @click="deleteDailyHusbandryRecord()" :loading="deleting">Delete</v-btn>
                        </v-col>
                        <v-col cols="12" v-if="deleteError">
                            <error-alert :content="deleteError" display="true"></error-alert>
                        </v-col>
                    </v-row>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script>
import ErrorAlert from "../common/ErrorAlert";
import constants from 'comunet-btg-utah-repository-shared/constants';
import * as auth from '@/services/auth';

import moment from 'moment';

export default {
    components: {
        ErrorAlert
    },
    props: {
        displaySpecimenID: {
            type: Boolean,
            default: false
        },
        // eslint-disable-next-line vue/require-default-prop
        searchCriteria: Object,
        displayDelete: {
            type: Boolean,
            default: true
        },
        displayUser: {
            type: Boolean,
            default: true
        },
        displayLocationWeight: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            auth: auth,
            showDeleteConfirmDialog: false,
            deletedDailyHusbandryRecord: {},
            totalHusbandryHistory: 0,
            husbandry: [],
            footerProps: {
                'items-per-page-options': [5, 20, 50, 100]
            },
            printNextLoad: false,
            pagination: {
                rowsPerPage: 20, // default
                sortBy: "EventDate",
                descending: true,
                page: 1
            },
            headers: [
                {
                    text: "Date",
                    value: "EventDate"
                },
                {
                    text: "User",
                    value: "User"
                },
                {
                    text: "Specimen ID",
                    value: "SpecimenDisplayID"
                },
                {
                    text: "Location",
                    value: "Location"
                },
                {
                    text: "Weight",
                    value: "Weight"
                },
                {
                    text: "Type",
                    value: "Type"
                },
                {
                    text: "Notes",
                    value: "Notes"
                },
                {
                    text: "", // delete button
                    value:"Delete",
                    sortable: false
                }
            ]
        };
    },
    filters: {
        formatTasksDate: function(value) {
            return moment.utc(value).format(constants.DATE_FORMAT_NO_TIME);
        },
        formatNonTasksDate: function(value) {
            return moment(value).format(constants.DATE_FORMAT);
        }
    },
    computed: {
        computedHeaders() {
            return this.headers
                .filter(x => (x.value != "Delete" && x.value != "User") || this.displayDelete || this.displayUser)
                .filter(x => x.value != "SpecimenDisplayID" || this.displaySpecimenID)
                .filter(x => (x.value != "Weight" && x.value != "Location") || this.displayLocationWeight);
        },
        loading() {
            return this.$store.state.dailyHusbandryStore.historyLoading;
        },
        loadError() {
            return this.$store.state.dailyHusbandryStore.historyLoadError;
        },
        deleting() {
            return this.$store.state.dailyHusbandryStore.deleting;
        },
        deleteError() {
            return this.$store.state.dailyHusbandryStore.deleteError;
        },
        search() {
            if (this.searchCriteria === undefined){
                return {};
            } else {
                return this.searchCriteria;
            }
        }
    },
    watch: {
        searchCriteria: {
            handler(newVal, oldVal) {
                // note that a strigify check is not technically very good (very dependent on key order), but in our case we're just looking to avoid spurious lookups and it suffices
                if (JSON.stringify(oldVal) != JSON.stringify(newVal)) {
                    this.loadDailyHusbandryHistory();
                }
            },
            deep: true
        },
        pagination: {
            handler() {
                this.loadDailyHusbandryHistory();
            },
            deep: true
        }
    },
    methods: {
        cancelDeleteDailyHusbandryRecord() {
            this.deletedDailyHusbandryRecord = {};
            this.showDeleteConfirmDialog = false;
        },
        confirmDeleteDailyHusbandryRecord(recordToDelete) {
            this.$store.dispatch("dailyHusbandryStore/clearDeleteError");
            this.deletedDailyHusbandryRecord = recordToDelete;
            this.showDeleteConfirmDialog = true;
        },
        deleteDailyHusbandryRecord() {
            let that = this;

            this.$store.dispatch("dailyHusbandryStore/deleteDailyHusbandryRecord", {
                record: this.deletedDailyHusbandryRecord,
                success: function() {
                    that.cancelDeleteDailyHusbandryRecord();

                    that.$emit("onSaved");
                }
            });
        },
        loadDailyHusbandryHistory() {
            if ((this.search.specimenID && this.search.specimenID > 0) ||
                (this.search.SearchTime)){
                let that = this;
                this.$store.dispatch("dailyHusbandryStore/loadHusbandryHistory", {
                    params: {
                        PaginationPage: this.pagination.page,
                        PaginationPageSize: this.pagination.rowsPerPage,
                        SortBy: this.pagination.sortBy,
                        SortDirection: this.pagination.descending ? "DESC" : "ASC",
                        SpecimenID: this.search.specimenID,
                        SpecimenDisplayID: this.search.specimenDisplayID,
                        SpeciesID: this.search.speciesID,
                        SupplierID: this.search.supplierID,
                        Gender: this.search.gender,
                        LogTypes: this.search.logTypes ? this.search.logTypes.join(',') : this.search.logTypes,
                        ArrivalMinDate: this.search.searchArrivalMinDate,
                        ArrivalMaxDate: this.search.searchArrivalMaxDate,
                        RangeMinDate: this.search.searchRangeMinDate,
                        RangeMaxDate: this.search.searchRangeMaxDate
                    },
                    success: (data) => {
                        // Decorate objects with necessary referenced objects
                        that.husbandry = data.HusbandryHistory;
                        that.totalHusbandryHistory = data.HusbandryHistoryTotal;
                    },
                    finally: () => {
                        that.specimensLoading = false;

                        if (that.printNextLoad){
                            that.$nextTick(() => 
                            {
                                window.print();
                                that.printMode(false);
                            });
                        }
                    }
                });
            } else {
                this.husbandry = [];
            }
        },
        refresh() {
            this.loadDailyHusbandryHistory();
        },
        printMode(enabled){
            if (enabled){
                this.printNextLoad = true;
                this.rowsPerPage = [5, 20, 50, 100, 1000]
                this.pagination.rowsPerPage = 1000;
            } else {
                this.rowsPerPage = [5, 20, 50, 100]
                this.pagination.rowsPerPage = 20;
                this.printNextLoad = false;
            }
        },
        determineHightlightClass(identifier){
            if (identifier.startsWith("DO")){
                return "dangerousOccurance";
            } else if (identifier.startsWith("CO")){
                return "controlledOccurance";
            } else {
                return "";
            }
        }
    },
    created() {
        this.$store.dispatch("dailyHusbandryStore/clearDeleteError");

        this.loadDailyHusbandryHistory();
    }
};
</script>

<style scoped>
    .display-new-line{
        white-space: pre-line;
    }

    tr.controlledOccurance td:first-child{
        border-left: 1em solid orange;
    }
    tr.dangerousOccurance td:first-child{
        border-left: 1em solid red;
    }
</style>
